import IconRenderer from '@/ui/Layout/Sidebar/IconRenderer';
import { NavigationItem } from '@/ui/Layout/Sidebar/NavigationItems/navigationItems';
import useHubTranslation from '@/utils/i18n/useHubTranslation';
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  useTheme,
} from '@mui/material';
import moment from 'moment-timezone';
import React from 'react';

interface FooterSectionProps {
  collapsed: boolean;
  onContactUs: () => void;
  onReleaseNotes: () => void;
  onGiveFeedback: () => void;
  onTermsOfUse: () => void;
  onPrivacyPolicy: () => void;
  showContactUs?: boolean;
  showReleaseNotes?: boolean;
  showGiveFeedback?: boolean;
}

// Shared styles for the Version, Terms of Use, Privacy Policy, and trademark
// captions so they render at a consistent size, line-height, and color.
const footerCaptionSx = {
  fontSize: '0.6875rem',
  lineHeight: 1.3,
  color: 'text.secondary',
} as const;

const footerNavigationItems: NavigationItem[] = [
  {
    label: 'Contact Us',
    url: '/contact',
    icon: {
      Icon: () => (
        <svg
          xmlns='http://www.w3.org/2000/svg'
          width='18'
          height='18'
          viewBox='0 0 18 18'
          fill='none'
        >
          <g clipPath='url(#clip0_296_18357)'>
            <path
              d='M8.25 17.6925V14.9925C4.4925 14.7975 1.5 11.6775 1.5 7.875C1.5 3.945 4.695 0.75 8.625 0.75C12.555 0.75 15.75 3.945 15.75 7.875C15.75 11.5875 13.17 15.3225 9.3225 17.175L8.25 17.6925ZM8.625 2.25C5.52 2.25 3 4.77 3 7.875C3 10.98 5.52 13.5 8.625 13.5H9.75V15.225C12.48 13.5 14.25 10.665 14.25 7.875C14.25 4.77 11.73 2.25 8.625 2.25ZM7.875 10.875H9.375V12.375H7.875V10.875ZM9.375 9.75H7.875C7.875 7.3125 10.125 7.5 10.125 6C10.125 5.175 9.45 4.5 8.625 4.5C7.8 4.5 7.125 5.175 7.125 6H5.625C5.625 4.3425 6.9675 3 8.625 3C10.2825 3 11.625 4.3425 11.625 6C11.625 7.875 9.375 8.0625 9.375 9.75Z'
              fill='#323232'
            />
          </g>
          <defs>
            <clipPath id='clip0_296_18357'>
              <rect width='18' height='18' fill='white' />
            </clipPath>
          </defs>
        </svg>
      ),
    },
  },
  {
    label: 'Release Notes',
    url: '/release-notes',
    icon: {
      Icon: () => (
        <svg
          xmlns='http://www.w3.org/2000/svg'
          width='18'
          height='18'
          viewBox='0 0 18 18'
          fill='none'
        >
          <path
            d='M10.6275 3.75L14.25 7.3725V14.25H3.75V3.75H10.6275ZM10.6275 2.25H3.75C2.925 2.25 2.25 2.925 2.25 3.75V14.25C2.25 15.075 2.925 15.75 3.75 15.75H14.25C15.075 15.75 15.75 15.075 15.75 14.25V7.3725C15.75 6.975 15.5925 6.5925 15.3075 6.315L11.685 2.6925C11.4075 2.4075 11.025 2.25 10.6275 2.25ZM5.25 11.25H12.75V12.75H5.25V11.25ZM5.25 8.25H12.75V9.75H5.25V8.25ZM5.25 5.25H10.5V6.75H5.25V5.25Z'
            fill='#323232'
          />
        </svg>
      ),
    },
  },
];

// Footer Section Component
const FooterSection: React.FC<FooterSectionProps> = ({
  collapsed,
  onContactUs,
  onReleaseNotes,
  onGiveFeedback,
  onTermsOfUse,
  onPrivacyPolicy,
  showContactUs = true,
  showReleaseNotes = true,
  showGiveFeedback = true,
}) => {
  const theme = useTheme();
  const { translateHubUi } = useHubTranslation();

  const thinDividerSx = {
    height: '1px',
    backgroundColor: theme.palette.divider,
  } as const;

  if (collapsed) {
    return null; // Hide footer in collapsed mode
  }

  const hasLinkRow = showContactUs || showReleaseNotes;

  return (
    <Box sx={{ flexShrink: 0 }}>
      <Box
        sx={{
          flexShrink: 0,
          pb: 2,
        }}
      >
        {hasLinkRow && (
          <>
            <Box
              sx={{
                width: '100%',
                ...thinDividerSx,
                mb: 1,
              }}
            />
            <Box
              sx={{
                width: '14rem',
                mx: 'auto',
              }}
            >
              <List disablePadding>
                {showContactUs && (
                  <ListItem disablePadding>
                    <ListItemButton
                      onClick={onContactUs}
                      sx={{
                        minHeight: 32,
                        px: 1,
                        py: 0.5,
                        borderRadius: theme.shape.borderRadius * 0.75,
                        color: theme.palette.text.secondary,
                        fontSize: '0.8125rem',
                        '&:hover': {
                          backgroundColor: theme.palette.action.hover,
                          color: theme.palette.text.primary,
                        },
                      }}
                    >
                      <ListItemIcon
                        sx={{
                          minWidth: 32,
                          color: 'inherit',
                          '& svg': { fontSize: '1rem' },
                        }}
                      >
                        <IconRenderer
                          icon={footerNavigationItems[0].icon}
                          size={16}
                        />
                      </ListItemIcon>
                      <ListItemText
                        primary={translateHubUi(
                          'nav.footer.contactUs',
                          footerNavigationItems[0].label
                        )}
                        sx={{
                          '& .MuiListItemText-primary': {
                            fontSize: '0.8125rem',
                            fontWeight: theme.typography.fontWeightMedium,
                          },
                        }}
                      />
                    </ListItemButton>
                  </ListItem>
                )}
                {showReleaseNotes && (
                  <ListItem disablePadding>
                    <ListItemButton
                      onClick={onReleaseNotes}
                      sx={{
                        minHeight: 32,
                        px: 1,
                        py: 0.5,
                        borderRadius: theme.shape.borderRadius * 0.75,
                        color: theme.palette.text.secondary,
                        fontSize: '0.8125rem',
                        '&:hover': {
                          backgroundColor: theme.palette.action.hover,
                          color: theme.palette.text.primary,
                        },
                      }}
                    >
                      <ListItemIcon
                        sx={{
                          minWidth: 32,
                          color: 'inherit',
                          '& svg': { fontSize: '1rem' },
                        }}
                      >
                        <IconRenderer
                          icon={footerNavigationItems[1].icon}
                          size={16}
                        />
                      </ListItemIcon>
                      <ListItemText
                        primary={translateHubUi(
                          'nav.footer.releaseNotes',
                          footerNavigationItems[1].label
                        )}
                        sx={{
                          '& .MuiListItemText-primary': {
                            fontSize: '0.8125rem',
                            fontWeight: theme.typography.fontWeightMedium,
                          },
                        }}
                      />
                    </ListItemButton>
                  </ListItem>
                )}
              </List>
            </Box>
          </>
        )}

        {showGiveFeedback && (
          <>
            {hasLinkRow ? (
              <Box
                sx={{
                  width: '14rem',
                  ...thinDividerSx,
                  my: 1,
                  mx: 'auto',
                }}
              />
            ) : (
              <Box
                sx={{
                  width: '100%',
                  ...thinDividerSx,
                  mb: 1,
                }}
              />
            )}
            <Box sx={{ width: '14rem', mx: 'auto' }}>
              <Box sx={{ mb: 1.5 }}>
                <Box
                  onClick={onGiveFeedback}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    py: 1,
                    px: 2,
                    backgroundColor: theme.palette.background.paper,
                    color: theme.palette.text.primary,
                    borderRadius: 2.5,
                    border: `1px solid ${theme.palette.grey[300]}`,
                    cursor: 'pointer',
                    fontSize: '0.8125rem',
                    fontWeight: theme.typography.fontWeightBold,
                    transition: theme.transitions.create([
                      'background-color',
                      'color',
                      'border-color',
                    ]),
                    '&:hover': {
                      backgroundColor: theme.palette.grey[100],
                      color: theme.palette.primary.main,
                      borderColor: theme.palette.primary.main,
                    },
                  }}
                >
                  {translateHubUi('nav.footer.giveFeedback', 'Give Feedback')}
                </Box>
              </Box>
            </Box>
          </>
        )}

        {hasLinkRow && !showGiveFeedback && (
          <Box
            sx={{
              width: '14rem',
              ...thinDividerSx,
              my: 1,
              mx: 'auto',
            }}
          />
        )}

        <Box sx={{ width: '14rem', mx: 'auto' }}>
          {/* Version */}
          <Box
            sx={{
              px: 1,
              py: 0.5,
              textAlign: 'left',
              mb: 1,
            }}
          >
            <Typography variant='caption' sx={footerCaptionSx}>
              {`Version: ${process.env.NEXT_PUBLIC_VERSION}`}
            </Typography>
          </Box>

          {/* Terms of Use and Privacy Policy */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'left',
              gap: 2,
              px: 1,
              mb: 1,
            }}
          >
            <Typography
              component='button'
              type='button'
              role='button'
              variant='caption'
              sx={{
                ...footerCaptionSx,
                fontFamily: 'inherit',
                fontWeight: 'inherit',
                cursor: 'pointer',
                textDecoration: 'none',
                background: 'none',
                border: 'none',
                padding: 0,
                '&:hover': {
                  textDecoration: 'underline',
                },
              }}
              onClick={onTermsOfUse}
            >
              {translateHubUi('nav.footer.termsOfUse', 'Terms of Use')}
            </Typography>
            <Typography
              component='button'
              type='button'
              role='button'
              variant='caption'
              sx={{
                ...footerCaptionSx,
                fontFamily: 'inherit',
                fontWeight: 'inherit',
                cursor: 'pointer',
                textDecoration: 'none',
                background: 'none',
                border: 'none',
                padding: 0,
                '&:hover': {
                  textDecoration: 'underline',
                },
              }}
              onClick={onPrivacyPolicy}
            >
              {translateHubUi('nav.footer.privacyPolicy', 'Privacy Policy')}
            </Typography>
          </Box>

          {/* Trademark */}
          <Box
            sx={{
              px: 1,
              textAlign: 'left',
            }}
          >
            <Typography variant='caption' sx={footerCaptionSx}>
              © {moment().year()} Cornerstone Information Systems, Inc.
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default FooterSection;
